import i18n from "../../i18n";

const API_BASE_URL =
  window.PARANOIDI_API_BASE_URL ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:4000/api"
    : "/backend/api");

function getStoredPerformerSlug() {
  try {
    return (
      JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
        ?.slug || ""
    );
  } catch {
    return "";
  }
}

function getApiErrorMessage(fi, en) {
  const language = String(
    i18n.resolvedLanguage || i18n.language || "fi"
  ).toLowerCase();

  return language.startsWith("en") ? en : fi;
}

function createApiError(fi, en, data = null) {
  const error = new Error(getApiErrorMessage(fi, en));

  error.code = data?.code || "";
  error.payload = data || {};
  error.backendMessage = String(data?.error || "").trim();

  return error;
}

function getRequiredPerformerSlug(performerSlug = null) {
  const slug = String(performerSlug || getStoredPerformerSlug() || "").trim();

  if (!slug) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu. Kirjaudu sisään uudelleen.",
      "Performer identifier is missing. Please log in again."
    );
  }

  return slug;
}

function getPublicPerformerBase(performerSlug = null) {
  const slug = getRequiredPerformerSlug(performerSlug);

  return `${API_BASE_URL}/public/performers/${encodeURIComponent(slug)}`;
}

/**
 * Hakee julkisen esiintyjän tiedot backendistä.
 */
export async function getPublicPerformer(performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const response = await fetchWithCredentials(publicBase);

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Esiintyjän tietojen haku epäonnistui.",
      "Could not load performer details.",
      data
    );
  }

  return data;
}

/**
 * Hakee kaikki biisitoiveet esiintyjäkohtaisesta PostgreSQL-reitistä.
 */
export async function getSongRequests(performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const response = await fetchWithCredentials(`${publicBase}/song-requests`);

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveiden haku epäonnistui.",
      "Could not load song requests.",
      data
    );
  }

  return data;
}

/**
 * Lisää uuden biisitoiveen esiintyjäkohtaisesti.
 */
export async function createSongRequest(payload, performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const response = await fetchWithCredentials(`${publicBase}/song-requests`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveen lähetys epäonnistui.",
      "Could not send the song request.",
      data
    );
  }

  return data;
}

/**
 * Päivittää biisitoiveen tilan esiintyjäkohtaisesti.
 */
export async function updateSongRequestStatus(id, status, performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const response = await fetchWithCredentials(`${publicBase}/song-requests/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify({ status }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveen päivitys epäonnistui.",
      "Could not update the song request.",
      data
    );
  }

  return data;
}

/**
 * Poistaa biisitoiveen esiintyjäkohtaisesti.
 */
export async function deleteSongRequest(id, performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const response = await fetchWithCredentials(`${publicBase}/song-requests/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveen poisto epäonnistui.",
      "Could not delete the song request.",
      data
    );
  }

  return data;
}

/**
 * Vanha slugittoman biisitoivelistan tyhjennysreitti ei ole käytössä SaaS-versiossa.
 * Biisitoiveet poistetaan esiintyjäkohtaisesti.
 */
export async function clearSongRequests() {
  throw createApiError(
    "Vanha biisitoiveiden tyhjennysreitti ei ole käytössä. Poista biisitoiveet esiintyjäkohtaisesti.",
    "The legacy route for clearing song requests is not available. Delete song requests per performer instead."
  );
}

/**
 * Hakee Paranoidin biisikirjaston esiintyjäkohtaisesti.
 */
export async function getSongs(performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const response = await fetchWithCredentials(`${publicBase}/songs`);

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisikirjaston haku epäonnistui.",
      "Could not load the song library.",
      data
    );
  }

  return data;
}

/**
 * Lisää monta biisiä kerralla esiintyjäkohtaiseen biisikirjastoon.
 */
export async function bulkCreateSongs(payload, performerSlug = null) {
  const slug = getRequiredPerformerSlug(performerSlug);

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/songs/bulk`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisien massa-ajo epäonnistui.",
      "Could not add the songs.",
      data
    );
  }

  return data;
}

/**
 * Päivittää yksittäisen biisin esiintyjäkohtaisessa biisikirjastossa.
 */
export async function updateSong(id, payload, performerSlug = null) {
  const slug = getRequiredPerformerSlug(performerSlug);

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisin päivitys epäonnistui.",
      "Could not update the song.",
      data
    );
  }

  return data;
}

/**
 * Poistaa biisin esiintyjäkohtaisesta biisikirjastosta.
 */
export async function deleteSong(id, performerSlug = null) {
  const slug = getRequiredPerformerSlug(performerSlug);

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisin poisto epäonnistui.",
      "Could not delete the song.",
      data
    );
  }

  return data;
}

/**
 * Vanha slugittoman biisin rikastusreitti ei ole käytössä SaaS-versiossa.
 */
export async function enrichSong() {
  throw createApiError(
    "Vanha biisin rikastusreitti ei ole käytössä SaaS-versiossa.",
    "The legacy song enrichment route is not available in the SaaS version."
  );
}

/**
 * Vanha slugittoman tiedostotuonnin esikatselu ei ole käytössä SaaS-versiossa.
 * Tiedostotuonti tehdään jatkossa vain esiintyjäkohtaisen Plus-tuonnin kautta.
 */
export async function previewSongImportFile(file, performerSlug = null) {
  const slug = String(performerSlug || getStoredPerformerSlug() || "").trim();

  if (!slug) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu.",
      "Performer identifier is missing."
    );
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/songs/import-file-preview`,
    {
      method: "POST",
      body: formData,
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Tiedoston esikatselu epäonnistui.",
      "Could not preview the file.",
      data
    );
  }

  return data?.preview || data?.item || data;
}

export async function enrichSongImportPreview() {
  throw createApiError(
    "Vanha esikatselun rikastusreitti ei ole käytössä SaaS-versiossa.",
    "The legacy preview enrichment route is not available in the SaaS version."
  );
}

/**
 * Hakee esiintyjän tapahtumat hallintaan.
 */
export async function getPerformerEvents(performerSlug = null) {
  const slug = String(performerSlug || getStoredPerformerSlug() || "").trim();

  if (!slug) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu",
      "Performer identifier is missing"
    );
  }

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/events`
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Tapahtumien haku epäonnistui.",
      "Could not load events.",
      data
    );
  }

  return data;
}

/**
 * Hakee julkisella sivulla voimassa olevat tapahtumat.
 */
export async function getPublicPerformerEvents(performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  if (!publicBase) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu",
      "Performer identifier is missing"
    );
  }

  const response = await fetchWithCredentials(`${publicBase}/events`);
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Tapahtumien haku epäonnistui.",
      "Could not load events.",
      data
    );
  }

  return data;
}

/**
 * Lisää uuden tapahtuman hallinnasta.
 */
export async function createPerformerEvent(payload, performerSlug = null) {
  const slug = String(performerSlug || getStoredPerformerSlug() || "").trim();

  if (!slug) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu",
      "Performer identifier is missing"
    );
  }

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/events`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Tapahtuman lisääminen epäonnistui.",
      "Could not add the event.",
      data
    );
  }

  return data;
}

/**
 * Päivittää hallinnan tapahtuman.
 */
export async function updatePerformerEvent(eventId, payload, performerSlug = null) {
  const slug = String(performerSlug || getStoredPerformerSlug() || "").trim();

  if (!slug) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu",
      "Performer identifier is missing"
    );
  }

  if (!eventId) {
    throw createApiError(
      "Tapahtuman tunniste puuttuu",
      "Event identifier is missing"
    );
  }

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/events/${encodeURIComponent(eventId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Tapahtuman päivittäminen epäonnistui.",
      "Could not update the event.",
      data
    );
  }

  return data;
}

/**
 * Poistaa tapahtuman hallinnasta.
 */
export async function deletePerformerEvent(eventId, performerSlug = null) {
  const slug = String(performerSlug || getStoredPerformerSlug() || "").trim();

  if (!slug) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu",
      "Performer identifier is missing"
    );
  }

  if (!eventId) {
    throw createApiError(
      "Tapahtuman tunniste puuttuu",
      "Event identifier is missing"
    );
  }

  const response = await fetchWithCredentials(
    `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/events/${encodeURIComponent(eventId)}`,
    {
      method: "DELETE",
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Tapahtuman poistaminen epäonnistui.",
      "Could not delete the event.",
      data
    );
  }

  return data;
}

/*
 * Admin-pyyntöjen autentikointi perustuu palvelimen HttpOnly-session-cookieen.
 * Lähetetään cookie mukana myös localhostin frontend -> backend -pyynnöissä.
 */
function fetchWithCredentials(input, init = {}) {
  return fetch(input, {
    ...init,
    credentials: "include",
  });
}